import type { ActorDTO, IdeaRowDTO, NodeDTO, TaskDTO } from "@roomate/core";
import { hasFinishedBuilding, isDropped } from "../idea-clock.ts";

export type IdeaStepKey = "received" | "spec" | "approval" | "building" | "review" | "shipped";
export type IdeaStepState = "done" | "current" | "upcoming";

export type IdeaStep = {
  key: IdeaStepKey;
  label: string;
  state: IdeaStepState;
  /** One plain sentence on what this step is doing right now. */
  detail: string;
  /** Who holds the step (a name), or null when nobody does. */
  holder: string | null;
  /** When the step started, or null when it has not. */
  since: number | null;
  /** Set only on the current step when nothing will move without a person acting. */
  stuck: { reason: string; action: { label: string; to: string } } | null;
};

export type IdeaStatusContext = { actors: readonly ActorDTO[]; nodes: readonly NodeDTO[] };

const STEP_LABELS: Record<IdeaStepKey, string> = {
  received: "Idea received",
  spec: "Spec",
  approval: "Your approval",
  building: "Building",
  review: "In review",
  shipped: "On main",
};
const ORDER: IdeaStepKey[] = ["received", "spec", "approval", "building", "review", "shipped"];

/** The step an idea is on right now. Shipped means merged; all tasks done but unmerged still reads as the last step, pending. */
export function currentIdeaStep(row: IdeaRowDTO): IdeaStepKey {
  const { intent, tasks } = row;
  if (row.shippedAt != null) return "shipped";
  // A dropped idea (every task cancelled) built nothing, whatever its intent says; it reads as the building step with the reason.
  if (isDropped(row)) return "building";
  if (hasFinishedBuilding(row)) return "shipped";
  if (intent.status === "approved") {
    const live = tasks.filter((t) => t.stage !== "cancelled" && t.stage !== "done");
    return live.length > 0 && live.every((t) => t.stage === "verifying" || t.stage === "review") ? "review" : "building";
  }
  if (intent.status === "spec_review") return "approval";
  return "spec";
}

/** Where an idea is, in the founder's words: who has the ball right now. Reads the same step model as the status panel. */
export function ideaStage(row: IdeaRowDTO): { step: number; label: string; who: "agent" | "you" | "agents" | "done" | "dropped" } {
  const { intent, tasks, specTask } = row;
  if (currentIdeaStep(row) === "shipped") return { step: 4, label: "Shipped", who: "done" };
  if (isDropped(row)) return { step: 3, label: "Dropped — every task was cancelled", who: "dropped" };
  if (intent.status === "approved") return { step: 3, label: `Building · ${tasks.filter((t) => t.stage === "done").length}/${tasks.length} done`, who: "agents" };
  if (intent.status === "spec_review") return { step: 2, label: "Spec ready — waiting for your approval", who: "you" };
  if (specTask?.stage === "blocked") return { step: 1, label: "spec-agent has a question for you", who: "you" };
  if (specTask?.builderId) return { step: 1, label: "spec-agent is writing the spec", who: "agent" };
  return { step: 0, label: "Filed — no spec agent available yet", who: "you" };
}

const name = (id: string | null | undefined, actors: readonly ActorDTO[]) => actors.find((a) => a.id === id)?.name ?? null;

/** Online machines that run agents (a merge-only node runs none). */
const machines = (nodes: readonly NodeDTO[]) => nodes.filter((n) => n.online && n.role !== "merge_only");

/** Why a task nobody is running will not move, as a stuck block; null when it is simply on its way. */
function whyTaskIsStuck(task: TaskDTO, ctx: IdeaStatusContext, what: string): IdeaStep["stuck"] {
  const agents = ctx.actors.filter((a) => a.kind === "agent" && a.active !== false);
  const holder = ctx.actors.find((a) => a.id === task.builderId) ?? null;
  const online = machines(ctx.nodes);
  if (task.stage === "blocked") {
    return { reason: `${what} is blocked on a question for a person.`, action: { label: "Answer it in Inbox", to: "/inbox" } };
  }
  if (task.stage === "done" || task.stage === "cancelled" || task.stage === "verifying" || task.stage === "review") return null;
  if (agents.length === 0) {
    return {
      reason: `No agent can ${what === "The spec" ? "write the spec" : "build this"}: this office has no agents yet${holder?.kind === "human" ? `, so it sits with ${holder.name}` : ""}.`,
      action: { label: "Add an agent on Team", to: "/team" },
    };
  }
  // A task someone is already working is not stuck, whoever that is.
  if (task.stage === "in_progress") return null;
  if (holder?.kind === "human") {
    return { reason: `${what} is assigned to ${holder.name}, a person, and no agent was picked for it.`, action: { label: `Open the task`, to: `/tasks/${task.id}` } };
  }
  if (online.length === 0) {
    return { reason: "No machine is online, so no agent can run.", action: { label: "Connect a machine", to: "/connect" } };
  }
  // A ready task nobody has claimed is on its way while an online machine runs an agent that can claim it: that gap is normal.
  // Only when no online machine runs any agent is it stuck. Areas and skills are not on this row, so eligibility is not judged here.
  if (!task.builderId && task.stage === "ready") {
    const claimable = online.some((n) => n.agentIds.some((id) => agents.some((a) => a.id === id)));
    if (claimable) return null;
    return { reason: `${what} is ready, but no online machine runs an agent to pick it up.`, action: { label: "Connect a machine", to: "/connect" } };
  }
  if (holder && !online.some((n) => n.agentIds.includes(holder.id))) {
    return { reason: `No online machine runs ${holder.name}, so it cannot start.`, action: { label: "Connect a machine", to: "/connect" } };
  }
  return null;
}

export function ideaSteps(row: IdeaRowDTO, ctx: IdeaStatusContext): IdeaStep[] {
  const { intent, tasks, specTask } = row;
  const current = currentIdeaStep(row);
  const at = ORDER.indexOf(current);
  const creator = name(intent.createdBy, ctx.actors);
  const counted = tasks.filter((t) => t.stage !== "cancelled");
  const done = counted.filter((t) => t.stage === "done").length;
  const inReview = counted.filter((t) => t.stage === "verifying" || t.stage === "review");

  const steps: IdeaStep[] = ORDER.map((key, i) => ({
    key,
    label: STEP_LABELS[key],
    state: i < at ? "done" : i === at ? "current" : "upcoming",
    detail: "",
    holder: null,
    since: null,
    stuck: null,
  }));
  const step = (key: IdeaStepKey) => steps[ORDER.indexOf(key)]!;

  const received = step("received");
  received.detail = `${creator ?? "Someone"} filed it.`;
  received.holder = creator;
  received.since = intent.createdAt;

  const spec = step("spec");
  const specHolder = name(specTask?.builderId, ctx.actors);
  if (spec.state === "current") {
    spec.since = specTask?.stageChangedAt ?? intent.createdAt;
    spec.holder = specHolder;
    if (specTask?.stage === "done") spec.detail = "The spec task is finished, but the spec has not been submitted for approval yet.";
    else if (specTask?.stage === "blocked") spec.detail = `${specHolder ?? "The spec writer"} has a question for you before it can finish the spec.`;
    else if (specHolder) spec.detail = `${specHolder} is ${ctx.actors.find((a) => a.id === specTask?.builderId)?.kind === "human" ? "meant to write" : "writing"} the spec.`;
    else spec.detail = "Waiting for a builder to pick up the spec task.";
    spec.stuck = specTask ? whyTaskIsStuck(specTask, ctx, "The spec") : null;
  } else if (spec.state === "done") {
    spec.detail = "The spec is written.";
    spec.holder = specHolder;
    spec.since = intent.createdAt;
  } else spec.detail = "A spec agent turns the idea into a plan.";

  const approval = step("approval");
  if (approval.state === "current") {
    approval.detail = "The spec is ready. Nothing gets built until you approve it.";
    approval.holder = creator;
    approval.since = intent.updatedAt;
    approval.stuck = { reason: "Waiting for your decision.", action: { label: "Review and approve the spec", to: `/intents/${intent.id}` } };
  } else if (approval.state === "done") {
    approval.detail = "Approved.";
    approval.since = intent.updatedAt;
  } else approval.detail = "You read the spec and approve it before any code is written.";

  const building = step("building");
  if (building.state === "current") {
    building.detail = counted.length === 0 ? "Approved. The spec is being split into tasks." : `${done} of ${counted.length} tasks done.`;
    const stuckOf = (t: TaskDTO) => whyTaskIsStuck(t, ctx, `Task #${t.number}`);
    const active = counted.find((t) => t.stage === "in_progress");
    const lead = active ?? counted.find((t) => t.stage !== "done");
    building.holder = name(lead?.builderId, ctx.actors);
    building.since = lead?.stageChangedAt ?? intent.updatedAt;
    // A question for a person shows even while siblings run; agent and machine problems only when nothing is moving.
    // Moving means a sibling is being built, checked or reviewed: the idea is not stalled while its work is in flight.
    const moving = counted.some((t) => t.stage === "in_progress" || t.stage === "verifying" || t.stage === "review");
    const asking = counted.find((t) => t.stage === "blocked");
    const waiting = counted.find((t) => stuckOf(t));
    if (asking) building.stuck = stuckOf(asking);
    else if (!moving && waiting) building.stuck = stuckOf(waiting);
    if (counted.length === 0 && tasks.length > 0) {
      building.detail = "Every task for this idea was cancelled.";
      building.stuck = { reason: "Every task for this idea was cancelled, so nothing will be built.", action: { label: "Open the idea", to: `/intents/${intent.id}` } };
    }
  } else if (building.state === "done") building.detail = `All ${counted.length} tasks built.`;
  else building.detail = "Agents build the tasks in parallel.";

  const review = step("review");
  if (review.state === "current") {
    review.detail = `${inReview.length} of ${counted.length} tasks waiting on a check or a reviewer.`;
    const first = inReview.find((t) => t.stage === "review") ?? inReview[0];
    const reviewers = (first?.reviewerIds ?? []).map((id) => ctx.actors.find((a) => a.id === id)).filter((a): a is ActorDTO => !!a);
    review.holder = reviewers[0]?.name ?? null;
    review.since = first?.stageChangedAt ?? null;
    if (first?.stage === "review") {
      // Only a person who has to act, or a missing reviewer, is stuck; an agent reviewing is on its way.
      const person = reviewers.find((r) => r.kind === "human");
      if (person) {
        review.stuck = { reason: `Waiting for ${person.name} to review task #${first.number}.`, action: { label: "Open the task", to: `/tasks/${first.id}` } };
      } else if (reviewers.length === 0) {
        review.stuck = { reason: `Task #${first.number} is in review but no reviewer is assigned.`, action: { label: "Assign a reviewer", to: `/tasks/${first.id}` } };
      }
    }
  } else if (review.state === "done") review.detail = "Checked and reviewed.";
  else review.detail = "Every task is verified, and a person reviews the risky ones.";

  const shipped = step("shipped");
  if (shipped.state === "current") {
    if (row.shippedAt != null) {
      shipped.detail = "On main.";
      shipped.since = row.shippedAt;
      shipped.state = "done";
    } else {
      shipped.detail = "All tasks are done; the merge queue is landing them on main.";
      shipped.since = counted.length > 0 ? Math.max(...counted.map((t) => t.stageChangedAt)) : null;
    }
  } else shipped.detail = "The work lands on main.";
  // Shipped is terminal: when it is merged every step before it is done and it is done too.
  if (shipped.state === "done") for (const s of steps) s.state = "done";
  return steps;
}

/** The one stuck step, if any: it drives the banner on Ideas, the dialog and the intent page. */
export function stuckStep(steps: readonly IdeaStep[]): IdeaStep | null {
  return steps.find((s) => s.state === "current" && s.stuck) ?? null;
}
